import React, { memo, useMemo } from "react"
import { Link, useStaticQuery, graphql } from "gatsby"

/**
 * Services, as a mosaic.
 *
 * Replaces the pinned scroll-driven ScrollServices section. That version spent
 * 280vh — about 2520px, a fifth of the page — advancing three groups of cards
 * one at a time, so a reader saw two services per screenful and had to scroll
 * the height of two viewports to see the rest. The mosaic shows all five at
 * once, in roughly a third of the height, and drops the scroll handler with it.
 *
 * Two tile types share the grid:
 *
 *   SERVICE tiles are the five disciplines, each linking to its own page. These
 *   carry the navigation the pinned section used to.
 *
 *   METRIC tiles are outcomes from the case studies, on a $signal ground so
 *   they read as punctuation between the images rather than as more cards.
 *
 * Interleaving them is the point: the proof sits among the services rather than
 * in a band of its own, so a reader scanning the grid cannot take in what we do
 * without also taking in what it produced.
 */

/**
 * ATTRIBUTED METRICS — every one of these is published on its own case study
 * page and is quoted verbatim here, client included. Do not edit a value, a
 * label or a client name; if one needs to change it changes on the case study
 * first.
 *
 * Deliberately NOT the three in S3 (40% Big Jerry's lead-to-installation, 99%
 * EcoZohm quote accuracy, 50% Nurtureful admin time). S3 sits two sections
 * above and repeating its numbers here would read as a shortage of them.
 */
const TILES = [
  {
    kind: "service",
    name: "61932592-01",
    wide: true, // the one deliberate asymmetry, as in S2
    eyebrow: "ERP implementation",
    title: "Odoo Ready Partner",
    body: "Your Odoo implementation automates the workflows you run by hand, shortens deployment, and scales as your business grows.",
    to: "/Odoo/implementationPartner",
  },
  {
    kind: "metric",
    value: "25%",
    label: "Higher quote-to-win ratio",
    client: "Big Jerry's Fencing",
  },
  {
    kind: "metric",
    value: "100%",
    label: "HR workflow automated",
    client: "Muscat Media Group",
  },
  {
    kind: "service",
    name: "14224-01",
    eyebrow: "ERP optimization",
    title: "Microsoft Dynamics 365",
    body: "Your Dynamics 365 estate connects to the rest of the Microsoft stack.",
    to: "/dynamics365/financeOperations",
  },
  // Tile copy is deliberately short. The non-flagship tiles clamp at two lines
  // in a ~337px column — about 90 characters — and every one of these ran to
  // ~125 and was truncated mid-sentence. Keep new copy under 90.
  {
    kind: "service",
    name: "4706200-01",
    eyebrow: "Web development",
    title: "Tailor-made solutions",
    body: "Your platform is built for how you work, not adapted from a template.",
    to: "/services/webdevelopment",
  },
  {
    kind: "service",
    name: "13_asadahasg1-01",
    eyebrow: "Mobile development",
    title: "Android and iOS apps",
    body: "Your Android and iOS apps ship on short cycles and hold up after launch.",
    to: "/services/mobiledevelopment",
  },
  {
    kind: "service",
    name: "13728-01",
    eyebrow: "AI solutions",
    title: "AI that does the work",
    body: "Your models are finetuned on your own data, behind workflows you already use.",
    to: "/services/aisolution",
  },
  {
    kind: "metric",
    value: "30%",
    label: "Faster project turnaround",
    client: "Orangeline Pakistan",
  },
]

const ServiceMosaic = () => {
  // maxWidth 900: the widest a tile gets is the two-column one at ~860px on a
  // 1440px screen, so this covers it at 1x and every narrower tile above it.
  const imageData = useStaticQuery(graphql`
    query ServiceMosaicImages {
      allFile(
        filter: {
          relativeDirectory: { eq: "services" }
          extension: { in: ["webp", "png", "jpg"] }
        }
      ) {
        nodes {
          name
          childImageSharp {
            fluid(maxWidth: 900, quality: 72) {
              src
              srcSet
              sizes
            }
          }
        }
      }
    }
  `)

  const img = useMemo(() => {
    const byName = {}
    imageData.allFile.nodes.forEach(n => {
      if (n.childImageSharp) byName[n.name] = n.childImageSharp.fluid
    })
    return name => byName[name]
  }, [imageData])

  return (
    <section className="sm-section" id="services">
      <div className="sm-inner">
        <p className="sm-eyebrow">Broader than a package</p>
        <h2 className="sm-heading">
          When what you need runs longer than a package.
        </h2>
        <p className="sm-accent">
          You still get it at the pace AI makes possible
        </p>

        <div className="sm-grid">
          {TILES.map(t =>
            t.kind === "metric" ? (
              <div className="sm-tile sm-tile--metric" key={t.client + t.value}>
                <span className="sm-metric__value">{t.value}</span>
                <span className="sm-metric__label">{t.label}</span>
                <span className="sm-metric__client">{t.client}</span>
              </div>
            ) : (
              <Link
                to={t.to}
                key={t.name}
                className={
                  "sm-tile sm-tile--service" +
                  (t.wide ? " sm-tile--wide" : "")
                }
              >
                <img
                  className="sm-tile__img"
                  src={img(t.name) && img(t.name).src}
                  srcSet={img(t.name) && img(t.name).srcSet}
                  sizes={img(t.name) && img(t.name).sizes}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <span className="sm-tile__scrim" aria-hidden="true" />
                <span className="sm-tile__body">
                  <span className="sm-tile__eyebrow">{t.eyebrow}</span>
                  <span className="sm-tile__title">{t.title}</span>
                  <span className="sm-tile__desc">{t.body}</span>
                  <span className="sm-tile__cta">See how this works &rarr;</span>
                </span>
              </Link>
            )
          )}
        </div>
      </div>
    </section>
  )
}

export default memo(ServiceMosaic)
